import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FreightForwarderBidService, FreightForwarderBidDto } from '../../../services/api/freight-forwarder-bid';
import { DealService } from '../../../services/api/deal';
import { RequisitesService, CompanyRequisites } from '../../../services/api/requisites';
import { LanguageService } from '../../../services/i18n/language';
import { AuthService } from '../../../services/auth/auth';
import { Subscription } from 'rxjs';
import { DocNumberPipe } from '../../../services/request-number';

@Component({
  selector: 'app-view-bid',
  standalone: true,
  imports: [CommonModule, RouterModule, DocNumberPipe],
  templateUrl: './view-bid.html',
  styleUrl: './view-bid.scss'
})
export class ViewBidComponent implements OnInit, OnDestroy {
  bidId: number | null = null;
  bid: FreightForwarderBidDto | null = null;
  loading = false;
  error: string | null = null;
  accepting = false;
  acceptError: string | null = null;
  dealCreated = false;
  showRequisitesWarning = false;

  /** True если пользователь — таможенный брокер (только просмотр, без кнопки «Принять») */
  isBroker = false;

  // Translations
  titleText = 'Просмотр ставки';
  backText = 'Назад к запросу';
  loadingText = 'Загрузка...';
  bidNumberText = 'Номер ставки';
  companyText = 'Компания';
  priceText = 'Цена';
  currencyText = 'Валюта';
  deliveryDaysText = 'Срок доставки';
  servicesText = 'Услуги';
  noteText = 'Примечание';
  daysText = 'дней';
  createdAtText = 'Дата создания';
  updatedAtText = 'Дата обновления';
  noServicesText = 'Услуги не указаны';
  noNoteText = 'Примечание отсутствует';
  acceptBidText = 'Принять ставку';
  acceptingText = 'Принятие ставки...';
  dealCreatedText = 'Сделка успешно создана';
  requisitesWarningText = 'Для принятия ставки необходимо заполнить реквизиты компании';
  goToProfileText = 'Перейти в профиль';
  cancelText = 'Отмена';
  bidAcceptedBadgeText = 'Ставка принята';
  mainInfoTitleText = 'Основная информация';
  priceInTengeLabelText = 'В тенге:';
  ratingLabelText = 'Рейтинг:';
  bestPriceLabelText = 'Самая выгодная ставка';
  pricePositionSuffixText = '-е место по цене';
  requisitesRequiredTitleText = 'Необходимо заполнить реквизиты';

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private bidService: FreightForwarderBidService,
    private dealService: DealService,
    private requisitesService: RequisitesService,
    private languageService: LanguageService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('ViewBid: Running on server (SSR), skipping initialization');
      return;
    }

    setTimeout(() => {
      if (!this.authService.isAuthenticated()) {
        console.warn('ViewBid: User not authenticated, redirecting to login');
        this.router.navigate(['/login']);
        return;
      }

      const user = this.authService.getUser();
      const roleCode = user?.role?.code;
      const isCargoOwner = roleCode === 'ROLE_CARGO_OWNER';
      const isCustomsBroker = roleCode === 'ROLE_CUSTOMS_BROKER';
      if (!isCargoOwner && !isCustomsBroker) {
        console.warn('ViewBid: User is not cargo owner or customs broker, redirecting to home');
        this.router.navigate(['/']);
        return;
      }
      this.isBroker = isCustomsBroker;

      const bidIdParam = this.route.snapshot.paramMap.get('bidId');
      
      if (bidIdParam) {
        this.bidId = +bidIdParam;
        this.loadBid(+bidIdParam);
        this.updateTranslations();

        this.subscriptions.add(
          this.languageService.getTranslations().subscribe(() => {
            this.updateTranslations();
          })
        );
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('cargoOwner.viewBid.title') || 'Просмотр ставки';
    this.backText = this.languageService.translate('cargoOwner.viewBid.back') || 'Назад к запросу';
    this.loadingText = this.languageService.translate('cargoOwner.viewBid.loading') || 'Загрузка...';
    this.bidNumberText = this.languageService.translate('cargoOwner.viewBid.bidNumber') || 'Номер ставки';
    this.companyText = this.languageService.translate('cargoOwner.viewBid.company') || 'Компания';
    this.priceText = this.languageService.translate('cargoOwner.viewBid.price') || 'Цена';
    this.currencyText = this.languageService.translate('cargoOwner.viewBid.currency') || 'Валюта';
    this.deliveryDaysText = this.languageService.translate('cargoOwner.viewBid.deliveryDays') || 'Срок доставки';
    this.servicesText = this.languageService.translate('cargoOwner.viewBid.services') || 'Услуги';
    this.noteText = this.languageService.translate('cargoOwner.viewBid.note') || 'Примечание';
    this.daysText = this.languageService.translate('cargoOwner.viewBid.days') || 'дней';
    this.createdAtText = this.languageService.translate('cargoOwner.viewBid.createdAt') || 'Дата создания';
    this.updatedAtText = this.languageService.translate('cargoOwner.viewBid.updatedAt') || 'Дата обновления';
    this.noServicesText = this.languageService.translate('cargoOwner.viewBid.noServices') || 'Услуги не указаны';
    this.noNoteText = this.languageService.translate('cargoOwner.viewBid.noNote') || 'Примечание отсутствует';
    this.acceptBidText = this.languageService.translate('cargoOwner.viewBid.acceptBid') || 'Принять ставку';
    this.acceptingText = this.languageService.translate('cargoOwner.viewBid.accepting') || 'Принятие ставки...';
    this.dealCreatedText = this.languageService.translate('cargoOwner.viewBid.dealCreated') || 'Сделка успешно создана';
    this.requisitesWarningText = this.languageService.translate('cargoOwner.viewBid.requisitesWarning') || 'Для принятия ставки необходимо заполнить реквизиты компании';
    this.goToProfileText = this.languageService.translate('cargoOwner.viewBid.goToProfile') || 'Перейти в профиль';
    this.cancelText = this.languageService.translate('cargoOwner.viewBid.cancel') || 'Отмена';
    this.bidAcceptedBadgeText = this.languageService.translate('cargoOwner.viewBid.bidAcceptedBadge') || this.bidAcceptedBadgeText;
    this.mainInfoTitleText = this.languageService.translate('cargoOwner.viewBid.mainInfoTitle') || this.mainInfoTitleText;
    this.priceInTengeLabelText = this.languageService.translate('cargoOwner.viewBid.priceInTengeLabel') || this.priceInTengeLabelText;
    this.ratingLabelText = this.languageService.translate('cargoOwner.viewBid.ratingLabel') || this.ratingLabelText;
    this.bestPriceLabelText = this.languageService.translate('cargoOwner.viewBid.bestPriceLabel') || this.bestPriceLabelText;
    this.pricePositionSuffixText = this.languageService.translate('cargoOwner.viewBid.pricePositionSuffix') || this.pricePositionSuffixText;
    this.requisitesRequiredTitleText = this.languageService.translate('cargoOwner.viewBid.requisitesRequiredTitle') || this.requisitesRequiredTitleText;
    this.cdr.markForCheck();
  }

  private loadBid(id: number): void {
    this.loading = true;
    this.error = null;

    this.subscriptions.add(
      this.bidService.getBid(id).subscribe({
        next: (bid) => {
          this.bid = bid;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('ViewBid: Error loading bid', err);
          if (err.status === 404) {
            this.error = 'Ставка не найдена';
          } else if (err.status === 403) {
            this.error = 'Ошибка доступа (403). Проверьте, что вы авторизованы.';
          } else if (err.status === 401) {
            this.error = 'Сессия истекла. Пожалуйста, войдите снова.';
          } else {
            this.error = 'Ошибка при загрузке ставки';
          }
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const lang = this.languageService.getCurrentLanguageValue();
    return date.toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getServiceName(service: { nameRu: string; nameEn: string }): string {
    const lang = this.languageService.getCurrentLanguageValue();
    return lang === 'en' ? service.nameEn : service.nameRu;
  }

  acceptBid(): void {
    if (!this.bidId || this.accepting || this.dealCreated) {
      return;
    }

    // Проверяем наличие реквизитов
    this.requisitesService.getMyRequisites().subscribe({
      next: (requisites) => {
        // Проверяем, заполнены ли реквизиты
        if (this.isRequisitesFilled(requisites)) {
          // Реквизиты заполнены, можно принять ставку
          this.proceedWithAcceptBid();
        } else {
          // Реквизиты не заполнены, показываем предупреждение
          this.showRequisitesWarning = true;
          this.cdr.markForCheck();
        }
      },
      error: (err) => {
        console.error('ViewBid: Error checking requisites', err);
        // Если реквизиты не найдены (404), показываем предупреждение
        if (err.status === 404) {
          this.showRequisitesWarning = true;
          this.cdr.markForCheck();
        } else {
          this.acceptError = 'Ошибка при проверке реквизитов';
          this.cdr.markForCheck();
        }
      }
    });
  }

  private isRequisitesFilled(requisites: CompanyRequisites): boolean {
    // Проверяем обязательные поля
    return !!(
      requisites &&
      requisites.bin &&
      requisites.legalAddress &&
      requisites.bankName &&
      requisites.bik &&
      requisites.contractSignerName &&
      requisites.contractSignerPosition &&
      requisites.bankAccounts &&
      requisites.bankAccounts.length > 0
    );
  }

  private proceedWithAcceptBid(): void {
    if (!this.bidId) {
      return;
    }

    this.accepting = true;
    this.acceptError = null;

    this.dealService.acceptBid(this.bidId).subscribe({
      next: () => {
        this.accepting = false;
        this.dealCreated = true;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('ViewBid: Error accepting bid', err);
        this.accepting = false;
        this.acceptError = err?.error?.message || 'Ошибка при принятии ставки';
        this.cdr.markForCheck();
      }
    });
  }

  goToProfile(): void {
    this.router.navigate(['/profile']);
  }

  closeRequisitesWarning(): void {
    this.showRequisitesWarning = false;
    this.cdr.markForCheck();
  }

  goBack(): void {
    if (!this.bid?.cargoRequestId) {
      if (this.isBroker) {
        this.router.navigate(['/customs-broker/dashboard']);
      } else {
        this.router.navigate(['/cargo-owner/dashboard/requests']);
      }
      return;
    }
    if (this.isBroker) {
      this.router.navigate(['/customs-broker/request', this.bid.cargoRequestId]);
    } else {
      this.router.navigate(['/cargo-owner/request', this.bid.cargoRequestId]);
    }
  }
}
